import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {SidePanelCloseButton} from './sidePanelCloseButton';

/*
 * Props.
 */

interface SidePanelHeaderProps {
  title: ReactNode;
  className?: string;
  additionalActionsBefore?: React.ReactElement;
  additionalActionsAfter?: React.ReactElement;
  shouldHideCloseButton?: boolean;
  onClose?: Handler;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  padding: 22px 16px 0 16px;
  padding-top: 14px;
  border-bottom: solid 1px ${(p) => p.theme.greys.shade30};

  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  padding-right: 6px;
`;

const StyledContentDiv = styled.div`
  margin-bottom: -4px;
  min-height: 17px;
`;

const StyledHeaderTopBarDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(0, max-content) auto minmax(30px, max-content);
  grid-template-areas: 'beforeActions title afterActions';
  align-items: center;
  margin-right: -9px;
`;

const StyledActionsDiv = styled.div`
  grid-area: afterActions;
  display: flex;
  flex-flow: row;
  margin-top: -6px;
  margin-bottom: -5px;
`;

const StyledActionsBeforeDiv = styled(StyledActionsDiv)`
  grid-area: beforeActions;
  margin-right: 8px;

  &:empty {
    margin: 0;
  }
`;

/*
 * Component.
 */

export const SidePanelHeader: FC<SidePanelHeaderProps> = ({
  title,
  children,
  additionalActionsBefore,
  additionalActionsAfter,
  className,
  shouldHideCloseButton,
  onClose,
}) => (
  <StyledHeaderDiv className={className}>
    <StyledHeaderTopBarDiv>
      {mayberRenderAdditionalActionsBefore(additionalActionsBefore)}
      <StyledTitleDiv>{title}</StyledTitleDiv>
      {mayberRenderAdditionalActionsAfter(Boolean(shouldHideCloseButton), additionalActionsAfter, onClose)}
    </StyledHeaderTopBarDiv>

    <StyledContentDiv>{children}</StyledContentDiv>
  </StyledHeaderDiv>
);

const mayberRenderAdditionalActionsBefore = (actions?: React.ReactElement) => {
  if (!actions) {
    return null;
  }
  return <StyledActionsBeforeDiv>{actions}</StyledActionsBeforeDiv>;
};

const mayberRenderAdditionalActionsAfter = (
  shouldHideCloseButton: boolean,
  actions?: React.ReactElement,
  onClose?: Handler,
) => {
  if (!actions && shouldHideCloseButton) {
    return null;
  }
  return (
    <StyledActionsDiv>
      {actions}
      {!shouldHideCloseButton && <SidePanelCloseButton onClose={onClose} />}
    </StyledActionsDiv>
  );
};
